// An AIpology — the stage: one draw call of signed-distance type and shapes over a shader backdrop,
// then trails, bloom, the mirror (the video's kaleidoscope), the crisp type, and the tape finish.
// The director (direct.js) fills it every frame.
(function () {
  const H = window.HANA;
  const G = H.G = {};
  const MAX = 16000;

  let renderer, composer, bgScene, bgCam, scene, tscene, camera, bgMat, bloom, finish, trail;
  let W = 1, Hh = 1, dpr = 1;

  // ---- the instance buffer: center, two half-axes, atlas rect, colour, style, effects
  // two layers: the world (bloomed, trailed) and the type on top (crisp, drawn after the bloom and the trails)
  const mkBuf = () => ({
    C: new Float32Array(MAX * 3), X: new Float32Array(MAX * 3), Y: new Float32Array(MAX * 3),
    UV: new Float32Array(MAX * 4), COL: new Float32Array(MAX * 4), ST: new Float32Array(MAX * 4), FX: new Float32Array(MAX * 4),
    n: 0, attrs: {}, geo: null
  });
  const LAYERS = [mkBuf(), mkBuf()];
  let B = LAYERS[0], n = 0;
  G.count = () => LAYERS[0].n + LAYERS[1].n;
  G.begin = () => { LAYERS[0].n = 0; LAYERS[1].n = 0; B = LAYERS[0]; };
  G.layer = i => { const prev = LAYERS.indexOf(B); B = LAYERS[i]; return prev; };
  // kind: 0 glyph, 1 rect, 2 disc, 3 sparkle, 4 star, 5 heart, 6 glow stick, 7 triangle, 8 halftone patch, 9 soft disc, 10 gradient bar
  // line: outline width (glyph: in distance units, ~0.04–0.2; shapes: world units); glow: halo strength; add: 0 normal, 1 additive
  G.quad = function (cx, cy, cz, ax, ay, az, bx, by, bz, uv, r, g, b, a, kind, line, glow, add, f0, f1, f2, f3) {
    const n = B.n;
    if (n >= MAX || a <= 0.002) return;
    const i3 = n * 3, i4 = n * 4;
    B.C[i3] = cx; B.C[i3 + 1] = cy; B.C[i3 + 2] = cz;
    B.X[i3] = ax; B.X[i3 + 1] = ay; B.X[i3 + 2] = az;
    B.Y[i3] = bx; B.Y[i3 + 1] = by; B.Y[i3 + 2] = bz;
    if (uv) { B.UV[i4] = uv.u0; B.UV[i4 + 1] = uv.v0; B.UV[i4 + 2] = uv.u1; B.UV[i4 + 3] = uv.v1; }
    else { B.UV[i4] = 0; B.UV[i4 + 1] = 0; B.UV[i4 + 2] = 0; B.UV[i4 + 3] = 0; }
    B.COL[i4] = r; B.COL[i4 + 1] = g; B.COL[i4 + 2] = b; B.COL[i4 + 3] = a;
    B.ST[i4] = kind; B.ST[i4 + 1] = line || 0; B.ST[i4 + 2] = glow || 0; B.ST[i4 + 3] = add || 0;
    B.FX[i4] = f0 || 0; B.FX[i4 + 1] = f1 || 0; B.FX[i4 + 2] = f2 || 0; B.FX[i4 + 3] = f3 || 0;
    B.n++;
  };

  const VERT = /* glsl */`
attribute vec3 aC, aX, aY;
attribute vec4 aUV, aCol, aSt, aFx;
varying vec2 vUv, vQ, vSize;
varying vec4 vCol, vSt, vFx;
void main(){
  vQ = position.xy;
  vUv = mix(aUV.xy, aUV.zw, vec2(uv.x, 1. - uv.y));
  vSize = vec2(length(aX), length(aY));
  vCol = aCol; vSt = aSt; vFx = aFx;
  vec3 p = aC + aX * position.x + aY * position.y;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.);
}`;
  const FRAG = /* glsl */`
uniform sampler2D uAtlas;
uniform float uEdge, uT;
varying vec2 vUv, vQ, vSize;
varying vec4 vCol, vSt, vFx;
float h21(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float sdStar5(vec2 p, float r, float rf){
  const vec2 k1 = vec2(0.809016994375, -0.587785252292);
  const vec2 k2 = vec2(-k1.x, k1.y);
  p.x = abs(p.x);
  p -= 2.0 * max(dot(k1, p), 0.0) * k1;
  p -= 2.0 * max(dot(k2, p), 0.0) * k2;
  p.x = abs(p.x);
  p.y -= r;
  vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0, 1);
  float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
  return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
}
float sdHeart(vec2 p){
  p.x = abs(p.x);
  if (p.y + p.x > 1.0) return sqrt(dot(p - vec2(0.25, 0.75), p - vec2(0.25, 0.75))) - sqrt(2.0) / 4.0;
  return sqrt(min(dot(p - vec2(0.00, 1.00), p - vec2(0.00, 1.00)), dot(p - 0.5 * max(p.x + p.y, 0.0), p - 0.5 * max(p.x + p.y, 0.0)))) * sign(p.x - p.y);
}
float fill(float sd){ float w = max(fwidth(sd), 1e-5) * .7; return 1. - smoothstep(-w, w, sd); }
void main(){
  float kind = vSt.x;
  float a = 0., glow = 0.;
  vec3 col = vCol.rgb;
  if (kind < .5) {
    float d = texture2D(uAtlas, vUv).r + vFx.z;
    if (vFx.y > 0.) d += (h21(floor(vUv * 900.) + floor(uT * 30.)) - .5) * vFx.y;   // a torn, noisy edge
    float w = max(fwidth(d) * .7, .003);
    float e = uEdge;
    if (vSt.y > 0.) {
      float o = vSt.y;
      a = smoothstep(e - o - w, e - o + w, d) * (1. - smoothstep(e + o * .35 - w, e + o * .35 + w, d));
    } else a = smoothstep(e - w, e + w, d);
    glow = smoothstep(.05, e, d); glow *= glow;
  } else {
    vec2 p = vQ * vSize;
    float sd = 1.;
    if (kind < 1.5) { vec2 r = vSize - vFx.x; vec2 q = abs(p) - r; sd = length(max(q, 0.)) + min(max(q.x, q.y), 0.) - vFx.x; }
    else if (kind < 2.5) { sd = (length(vQ) - 1.) * min(vSize.x, vSize.y); }
    else if (kind < 3.5) { vec2 q = abs(vQ); sd = (sqrt(q.x) + sqrt(q.y) - 1.) * min(vSize.x, vSize.y) * .5; }
    else if (kind < 4.5) { sd = sdStar5(vQ * vec2(1., -1.) * 1.05 + vec2(0., -.05), .95, .45) * min(vSize.x, vSize.y); }
    else if (kind < 5.5) { sd = sdHeart(vQ * vec2(.62, .62) + vec2(0., .5)) * min(vSize.x, vSize.y); }
    else if (kind < 6.5) { a = pow(max(0., 1. - abs(vQ.x)), 2.) * smoothstep(1., .55, abs(vQ.y)); glow = 1.; }
    else if (kind < 7.5) { vec2 q = vQ; sd = max(abs(q.x) * .866 + q.y * .5, -q.y) - .5; sd *= min(vSize.x, vSize.y); }
    else if (kind < 8.5) {
      vec2 g = p / max(vFx.x, 1e-3);
      vec2 c = fract(mat2(.707, -.707, .707, .707) * g) - .5;
      float rad = .5 * clamp(vFx.y + vFx.w * vQ.y, 0., 1.);
      a = fill(length(c) - rad) * (1. - smoothstep(.9, 1., max(abs(vQ.x), abs(vQ.y))));
    }
    else if (kind < 9.5) { float r = length(vQ); a = pow(max(0., 1. - r), 1.6); glow = a; }
    else { a = smoothstep(-1., 1., vQ.y * vFx.x) * smoothstep(1., .6, abs(vQ.x)); }
    if (kind < 5.5 || (kind > 6.5 && kind < 7.5)) {
      if (vSt.y > 0.) sd = abs(sd + vSt.y * .5) - vSt.y * .5;
      a = fill(sd);
      glow = exp(-max(sd, 0.) / (.04 * min(vSize.x, vSize.y) + 1e-4));
    }
  }
  float A = vCol.a * a;
  vec3 C = col * A + col * vSt.z * glow * vCol.a * (1. - a);
  gl_FragColor = vec4(C, A * (1. - vSt.w));
}`;

  // ---- the backdrop: one shader, many worlds
  // ---- the backdrop: one shader, many rooms of the facility
  const BG_FRAG = /* glsl */`
uniform vec2 uRes;
uniform float uT, uMode, uKick, uBeat, uRoll, uZoom, uSeed, uAmt, uM;
uniform vec3 uA, uB, uC;
uniform vec4 uP;
varying vec2 vUv;
float h21(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y); }
float fbm(vec2 p){ float s = 0., a = .5; for (int i = 0; i < 5; i++) { s += a * noise(p); p = p * 2.03 + 17.1; a *= .5; } return s; }
mat2 rot(float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
float line(float d, float w){ return 1. - smoothstep(w * .5, w * .5 + 1.5 / uRes.y, abs(d)); }
vec3 hue(float x){ return .5 + .5 * cos(6.2832 * (x + vec3(0., .33, .67))); }

// the optic's light through a lens: an anamorphic streak split into its colours, a turning starburst, ghosts down the frame
vec3 streaks(vec2 p, vec2 cp, float R, float t){
  vec2 d = p - cp;
  float fl = .6 + .4 * h21(vec2(floor(t * 24.), 9.));
  float w = mix(.011, .005, uM), sep = .007 * uM;
  vec3 sp = vec3(exp(-abs(d.y - sep) / w), exp(-abs(d.y) / w), exp(-abs(d.y + sep) / w)) * exp(-abs(d.x) * mix(1.4, .38, uM));
  vec3 col = mix(uC, vec3(1., .95, .9), .25);
  vec3 c = sp * col * (.6 + 1.6 * uM) * fl;
  c.r += exp(-abs(d.y - sep * 2.2) / (w * 1.6)) * exp(-abs(d.x) * .5) * .35 * uM * fl;
  c.b += exp(-abs(d.y + sep * 2.2) / (w * 1.6)) * exp(-abs(d.x) * .5) * .3 * uM * fl;
  c += uC * exp(-abs(d.x) / (w * .8)) * exp(-abs(d.y) * 3.) * .3 * uM * fl;
  float a = atan(d.y, d.x), r = length(d);
  float spk = pow(abs(cos(a * 3. + t * .4)), 260.);
  c += uC * spk * exp(-r / (R * (.6 + 1.2 * uM))) * (.2 + .6 * uM) * fl;
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    vec2 gp = -cp * (.5 + fi * .55) + vec2(0., 0.);
    float gr = R * (.06 + .1 * h21(vec2(fi, 2.)));
    float gd = length(p - gp);
    c += mix(uC, hue(fi * .19 + .55), .3) * smoothstep(gr, gr * .5, gd) * .07 * uM;
    c += mix(uC, hue(fi * .19 + .05), .3) * line(gd - gr * 1.4, .004) * .18 * uM;
  }
  return c;
}

// a box room seen from inside: x walls, floor and ceiling, a far wall; returns hit distance, face, surface coords
float box(vec3 ro, vec3 rd, vec2 hs, float far, out vec3 h, out float face){
  float tx = (sign(rd.x) * hs.x - ro.x) / (abs(rd.x) < 1e-4 ? 1e-4 : rd.x);
  float ty = (sign(rd.y) * hs.y - ro.y) / (abs(rd.y) < 1e-4 ? 1e-4 : rd.y);
  float tz = (-far - ro.z) / min(rd.z, -1e-4);
  float th = min(min(tx, ty), tz);
  h = ro + rd * th;
  face = th == tx ? 0. : th == ty ? (rd.y > 0. ? 1. : 2.) : 3.;
  return th;
}

// the test chamber: white panels, dark seams, a strip light, a few panels gone
vec3 chamber(vec2 p, float t){
  vec3 ro = vec3(0., 0., 0.);
  vec3 rd = normalize(vec3(p, -1.5));
  rd.xz = rot(uP.w) * rd.xz;
  vec3 h; float face;
  float th = box(ro, rd, vec2(2.4, 1.5), 16., h, face);
  float zz = h.z + t * uP.x;
  vec2 uv = face < .5 ? vec2(zz * sign(h.x), h.y) : face < 2.5 ? vec2(h.x, zz) : h.xy;
  float g = .62;
  vec2 cell = floor(uv / g), f = fract(uv / g);
  float e = min(min(f.x, 1. - f.x), min(f.y, 1. - f.y));
  float seam = 1. - smoothstep(.0, .04, e);
  float shade = .82 + .18 * h21(cell + face * 7.);
  float gone = step(.94, h21(cell * 1.7 + face + 3.)) * uP.y;
  vec3 c = uA * shade;
  c = mix(c, uB, seam * .85);
  c = mix(c, uB * .25, gone);
  if (face > 1.5 && face < 2.5) c *= .5;                                  // the floor, darker
  if (face > .5 && face < 1.5) {                                          // the ceiling: a strip light down the middle
    float strip = smoothstep(.16, .1, abs(h.x)) * step(.35, fract(zz * .22));
    c = mix(c, uC * 1.6, strip);
  }
  float fog = exp(-th * .075);
  c = mix(uB * .12, c, fog);
  c *= .55 + .65 * exp(-length(p) * 1.1);
  return c;
}

// the optic: housing, iris blades, a glowing core that looks around
vec3 optic(vec2 p, float t){
  float R = uP.x;
  // it shakes in bursts
  float burst = step(.55, h21(vec2(floor(t * 5.), 5.))) * uM;
  p += (vec2(h21(vec2(floor(t * 30.), 1.)), h21(vec2(floor(t * 30.), 2.))) - .5) * .018 * burst;
  vec2 p0 = p;
  float r = length(p), ang = atan(p.y, p.x);
  vec3 c = mix(uA * .22, uA * .04, smoothstep(0., 1.3, r));
  // a reticle around it
  float rr = r / R;
  c += uC * .1 * line(fract(ang / 6.2832 * 36. + t * .02) - .5, .03) * step(1.45, rr) * step(rr, 1.6);
  c += uC * .18 * line(rr - 1.7, .006 / R) * step(.5, fract(ang / 6.2832 * 3. - t * .05));
  c += uC * .35 * exp(-max(0., rr - 1.25) * 3.) * .35;                    // its light on the dark
  c += uC * .22 * uM * pow(max(0., cos(ang - t * 1.9)), 300.) * smoothstep(1.2, 1.5, rr) * exp(-(rr - 1.2) * .6);   // a sweep
  // the housing ratchets round, a notch at a time
  float tick = (floor(t * 1.3) + smoothstep(0., .07, fract(t * 1.3))) * .3927 * uM;
  // the housing: a brushed ring in eight segments
  float hous = smoothstep(1.27, 1.25, rr) * smoothstep(.99, 1.01, rr);
  float gap = line(fract((ang + tick) / 6.2832 * 8. + .0625) - .5, .025);
  vec3 metal = mix(vec3(.08, .09, .1), vec3(.62, .64, .65), .5 + .5 * sin(ang + 2.2));
  metal = mix(metal, uC * .7, line(fract((ang + tick) / 6.2832 * 8. + .5625) - .5, .01) * uM);
  metal *= .85 + .15 * sin(ang * 90.);
  metal = mix(metal, vec3(.02), gap * .9);
  c = mix(c, metal, hous);
  c = mix(c, vec3(0.), line(rr - 1.0, .012 / R) * .8);
  if (rr < 1.) {
    vec2 q = p - uP.zw * R * .28;                                         // the core follows its gaze
    float lr = length(q), la = atan(q.y, q.x);
    // the aperture: a hexagon of blades opening to A
    float N = 6., s = 6.2832 / N;
    float A = R * mix(.05, .6, clamp(uP.y, 0., 1.));
    A *= 1. + uM * .45 * (h21(vec2(floor(t * 9.), 3.)) - .5) * step(.5, h21(vec2(floor(t * 2.7), 4.)));   // spasms
    float bt = t * .1 + tick * 1.5;
    float hex = lr * cos(s * .5) / cos(mod(la + bt, s) - s * .5);
    float blade = fract((la + bt) / s + lr / R * 1.3);
    vec3 bl = vec3(.06, .065, .07) + vec3(.07) * blade + uC * .04 * (1. - rr);
    bl = mix(bl, vec3(.01), line(blade - .02, .03) * .8);
    float open = smoothstep(A + .003, A - .003, mix(lr, hex, .35));
    float u = lr / max(A, 1e-3);
    vec3 core = uC * (.18 + .55 * exp(-u * 2.6)) + vec3(1., .95, .9) * exp(-u * 9.) * .7;
    core += uC * .35 * smoothstep(.85, .98, u);                              // the rim of the aperture
    core *= .9 + .1 * sin(u * 18. - t * 5.);
    core *= 1. + uM * (.5 * h21(vec2(floor(t * 30.), 7.)) - .15);
    c = mix(bl, core, open);
    // a lens: one curved highlight
    float hl = smoothstep(.06, .0, abs(rr - .78)) * smoothstep(.4, 1., -sin(ang - .8));
    c += vec3(.9, .95, 1.) * hl * .18;
  }
  c += streaks(p, uP.zw * R * .28, R, t);
  return c;
}

// the datacenter: racks of blinking lights down a long cold aisle
vec3 servers(vec2 p, float t){
  vec3 rd = normalize(vec3(p, -1.35));
  vec3 h; float face;
  float th = box(vec3(0.), rd, vec2(1.25, 1.05), 40., h, face);
  float zz = h.z + t * uP.x;
  vec3 c = vec3(.02, .025, .03);
  if (face < .5) {
    float col = zz / 1.1, ci = floor(col), cf = fract(col);
    float rail = step(cf, .05) + step(.95, cf);
    float u = h.y / .085, ui = floor(u), uf = fract(u);
    c = vec3(.03, .035, .04) + vec3(.02) * step(.9, uf);
    vec2 id = vec2(floor(cf * 9.) + ci * 9. + sign(h.x) * 999., ui);
    vec2 lf = fract(vec2(cf * 9., u)) - .5;
    float rate = 2. + 10. * h21(id + 5.);
    float on = step(.45, h21(id + floor(t * rate) * .13));
    vec3 lc = h21(id + 9.) < .7 ? uA : uB;
    float dot = smoothstep(.22, .08, length(lf * vec2(1., 1.6)));
    c += lc * dot * on * 2.2 * step(.15, cf) * step(cf, .85);
    c = mix(c, vec3(.08, .09, .1), rail);
  } else if (face < 1.5) {
    c = vec3(.015);
    c += uC * smoothstep(.12, .05, abs(h.x)) * step(.4, fract(zz * .18)) * 1.5;
  } else if (face < 2.5) {
    c = vec3(.012, .014, .016) + vec3(.03) * line(fract(zz * .6) - .5, .02);
    c += uA * .05 * smoothstep(.6, 1.25, abs(h.x));
    c += uC * .12 * smoothstep(.3, .0, abs(h.x)) * step(.4, fract(zz * .18));
  }
  float fog = exp(-th * .06);
  c = mix(uA * .04, c, fog);
  return c;
}

// the fantasy: a pastel sky, clouds, a soft sun, a calm sea
vec3 dream(vec2 p, float t){
  vec3 c = mix(uB, uA, smoothstep(-.05, .75, p.y));
  vec2 sp = p - vec2(uP.x, .14);
  c += uC * exp(-length(sp) * 7.) * .3;
  c = mix(c, uC * 1.05 + .08, smoothstep(.085, .08, length(sp)));
  if (p.y > -.06) {
    vec2 q = p * vec2(1.4, 3.2) + vec2(t * .025, 0.);
    float cl = fbm(q * 1.2 + vec2(0., t * .01));
    float m = smoothstep(.56, .74, cl) * smoothstep(-.06, .18, p.y);
    vec3 cc = mix(vec3(.96, .93, .92), uB, .3) * (.75 + .3 * fbm(q * 3. + 3.));
    cc = mix(cc, uA * .9, smoothstep(.7, .5, cl) * .35);
    c = mix(c, cc, m * .92);
  } else {
    float d = -p.y;
    c = mix(uB * .92, uA * .75, smoothstep(.0, .55, d));
    float rip = sin(p.x * 30. / (d + .05) + t * 1.6 + fbm(p * vec2(4., 30.)) * 6.);
    c += vec3(.06) * rip * smoothstep(.0, .3, d);
    c += uC * smoothstep(.08, .0, abs(p.x - uP.x)) * (.5 + .5 * rip) * .45 * smoothstep(.6, .0, d);
  }
  return c;
}

// the line goes up: graph paper, a price, candles
vec3 chart(vec2 p, float t){
  vec3 c = uA;
  vec2 g = p / .06;
  c = mix(c, uB, line(fract(g.x + t * uP.y / .06) - .5, .03) * .25);
  c = mix(c, uB, line(fract(g.y) - .5, .03) * .25);
  float xw = p.x + t * uP.y;
  float head = .38;
  float y = uP.x * (p.x - head) + (fbm(vec2(xw * 2.4, 3.)) - .5) * .5 - .05;
  y -= uP.z * max(0., p.x - head + .5) * 2.4;                            // the crash
  float w = .006 + .004 * uKick;
  float d = abs(p.y - y) / sqrt(1. + uP.x * uP.x);
  float on = step(p.x, head);
  c = mix(c, uC * .25 + c * .7, smoothstep(.0, -.5, p.y - y) * on * .6);
  c += uC * (1. - smoothstep(w, w + .004, d)) * on * 1.6;
  c += uC * exp(-d * 40.) * on * .35;
  // candles along the line
  float cx = floor(xw / .05);
  float cfx = fract(xw / .05) - .5;
  float ch = .03 + .07 * h21(vec2(cx, 1.));
  float up = step(.35, h21(vec2(cx, 2.)));
  float body = step(abs(cfx), .28) * step(abs(p.y - y - (h21(vec2(cx, 3.)) - .5) * .06), ch);
  c = mix(c, up > .5 ? uC : vec3(.95, .25, .2), body * on * .35);
  // the head
  vec2 hp = vec2(head, uP.x * 0. + (fbm(vec2((head + t * uP.y) * 2.4, 3.)) - .5) * .5 - .05 - uP.z * .5 * 2.4);
  c += uC * exp(-length(p - hp) * 30.) * 1.5;
  return c;
}

// a tunnel to dive through
vec3 tunnel(vec2 p, float t){
  float r = length(p), a = atan(p.y, p.x);
  float z = uP.y / (r + .03) + t * uP.x;
  vec2 id = floor(vec2(z * 2., a / 6.2832 * 24.));
  vec2 f = fract(vec2(z * 2., a / 6.2832 * 24.));
  float lit = step(.82, h21(id + floor(t * 3.) * .0));
  vec3 c = mix(uA, uB, h21(id + 3.) * .6);
  c = mix(c, uC * 1.5, lit * step(.2, f.x) * step(f.x, .8) * step(.3, f.y) * step(f.y, .7));
  c *= 1. - .85 * max(line(f.x - .5, .06), line(f.y - .5, .05));
  c *= smoothstep(0., .6, r);
  return c;
}

// a galaxy, spinning, made of light
vec3 galaxy(vec2 p, float t){
  vec3 c = uA * .06;
  vec2 q = rot(t * uP.x) * p * vec2(1., 1.9);
  float r = length(q), a = atan(q.y, q.x);
  float arm = sin(a * 2. - log(r + .02) * 4.2);
  float dens = smoothstep(.2, 1., arm) * exp(-r * 2.2) * (.6 + .6 * fbm(q * 6.));
  c += mix(uB, uC, smoothstep(.1, .6, r)) * dens * 1.4;
  c += vec3(1., .95, .9) * exp(-r * 9.) * 1.2;
  vec2 sg = floor(gl_FragCoord.xy / 2.);
  c += step(.996, h21(sg)) * (.5 + .5 * sin(t * 3. + h21(sg + 1.) * 40.));
  return c;
}

// attention: a matrix of who-looks-at-whom, masked, filling in
vec3 attention(vec2 p, float t){
  vec3 c = uA;
  float N = uP.x;
  vec2 g = p / 1.5 + .5;
  if (g.x > 0. && g.x < 1. && g.y > 0. && g.y < 1.) {
    vec2 cell = floor(g * N), f = fract(g * N);
    float row = N - 1. - cell.y;
    float masked = step(row + .5, cell.x);
    float v = pow(h21(cell + floor(t * 4.) * .0 + 1.), 3.);
    v = max(v, step(abs(cell.x - row), .5) * .8);
    float shown = step(row, uP.y * N);
    float hot = step(abs(row - floor(uP.z)), .5);
    vec3 cc = mix(uB * .3, uC, v) * (1. + hot * .8);
    cc = mix(cc, uA * 1.4, masked);
    float gap = step(.08, f.x) * step(.08, f.y);
    c = mix(c, cc, shown * gap);
  }
  return c;
}

void main(){
  vec2 p = (gl_FragCoord.xy - .5 * uRes) / uRes.y;
  p = rot(uRoll) * p / uZoom;
  float t = uT;
  int m = int(uMode + .5);
  vec3 c = uA;
  if (m == 0) {                       // void
    c = uA * (1. - .6 * length(p)) + (h21(gl_FragCoord.xy + fract(t) * 100.) - .5) * .03;
  } else if (m == 1) c = chamber(p, t);
  else if (m == 2) c = optic(p, t);
  else if (m == 3) c = servers(p, t);
  else if (m == 4) c = dream(p, t);
  else if (m == 5) c = chart(p, t);
  else if (m == 6) {                  // static
    float s = h21(floor(gl_FragCoord.xy / 2.) + floor(t * 30.) * 7.1);
    c = mix(uA, uB, s) * (.75 + .25 * sin(p.y * 300. + t * 50.));
    c *= 1. - .5 * smoothstep(.0, .08, abs(fract(p.y * .5 - t * .4) - .5) - .38);
  }
  else if (m == 7) c = tunnel(p, t);
  else if (m == 8) {                  // paper
    c = uA * (.92 + .08 * fbm(p * 40.));
    c = mix(c, uB, line(fract(p.y * 9.) - .5, .02) * .12);
    c *= 1. - .25 * length(p);
  }
  else if (m == 9) c = attention(p, t);
  else if (m == 10) c = galaxy(p, t);
  else {                              // the feed: soft light in the dark
    c = mix(uA * .2, uB * .25, smoothstep(.8, -.6, p.y));
    for (int i = 0; i < 12; i++) {
      float fi = float(i);
      vec2 o = vec2(h21(vec2(fi, 1.)) - .5, fract(h21(vec2(fi, 2.)) + t * (.03 + .04 * h21(vec2(fi, 3.)))) - .5) * vec2(2., 1.4);
      float r = .05 + .12 * h21(vec2(fi, 4.));
      c += (mod(fi, 2.) < 1. ? uA : uC) * smoothstep(r, r * .6, length(p - o)) * .12;
    }
  }
  gl_FragColor = vec4(c * uAmt, 1.);
}`;

  // ---- the finish
  const FINISH = {
    uniforms: {
      tDiffuse: { value: null }, uRes: { value: new THREE.Vector2(1, 1) }, uT: { value: 0 }, uSeed: { value: 0 },
      uRGB: { value: 0 }, uSlice: { value: 0 }, uBlock: { value: 0 }, uFlash: { value: 0 }, uInv: { value: 0 },
      uScan: { value: 0.3 }, uGrain: { value: 0.06 }, uVig: { value: 0.3 }, uTint: { value: new THREE.Vector3(1, 1, 1) }, uDot: { value: 0 }, uDuo: { value: 0 },
      uPanels: { value: 0 }, uZB: { value: 0 }, uImpact: { value: 0 }, uBox: { value: 0 },
      uVhs: { value: 0 }, uLift: { value: 0 }, uSat: { value: 1 }
    },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }',
    fragmentShader: /* glsl */`
uniform sampler2D tDiffuse; uniform vec2 uRes; uniform float uT, uSeed, uRGB, uSlice, uBlock, uFlash, uInv, uScan, uGrain, uVig, uDot, uDuo, uPanels, uZB, uImpact, uBox, uVhs, uLift, uSat;
uniform vec3 uTint;
varying vec2 vUv;
float h(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main(){
  vec2 q = vUv;
  // split screen: three mirrored strips, or the whole frame four times
  float pid = 0., divider = 0.;
  if (uPanels > 2.5 && uPanels < 3.5) {
    pid = floor(q.x * 3.);
    float fx = fract(q.x * 3.);
    if (mod(pid, 2.) > .5) fx = 1. - fx;
    q = vec2(.5 + (fx - .5) / 3., vUv.y);
    divider = step(abs(fract(vUv.x * 3. + .5) - .5), 1.5 / uRes.x);
  } else if (uPanels > 3.5) {
    vec2 g = floor(vUv * 2.);
    pid = g.x + g.y * 2.;
    q = fract(vUv * 2.);
    divider = step(min(abs(vUv.x - .5), abs(vUv.y - .5)), 1.5 / uRes.y);
  }
  // macroblocks knocked out of place
  vec2 bq = floor(q * vec2(24., 14.));
  float bh = h(bq + uSeed * 1.37);
  float blk = step(1. - uBlock * .35, bh);
  q += blk * (vec2(h(bq + uSeed + 1.), h(bq + uSeed + 2.)) - .5) * vec2(.2, .05);
  // torn scanline slices
  float band = floor(q.y * 48. + h(vec2(uSeed, 3.)) * 20.);
  float on = step(1. - uSlice * .5, h(vec2(band, uSeed)));
  q.x += on * (h(vec2(band, uSeed + 3.)) - .5) * .18;
  // tape: a tracking band rolls down the picture, its lines slip; the whole frame wobbles a little
  float bandY = fract(uT * .061 + .3);
  float inBand = smoothstep(.07, .0, abs(vUv.y - bandY)) * uVhs;
  q.x += (h(vec2(floor(vUv.y * 180.), floor(uT * 30.))) - .5) * .035 * inBand;
  q.x += sin(vUv.y * 30. + uT * 4.) * .0012 * uVhs;
  // colour split: sideways, wider toward the edges
  vec2 d = vec2(1., .15) * uRGB + (q - .5) * uRGB * .6;
  vec3 c = vec3(texture2D(tDiffuse, q + d).r, texture2D(tDiffuse, q).g, texture2D(tDiffuse, q - d).b);
  // zoom blur: the punch-in smears toward the centre
  if (uZB > 0.) {
    vec3 acc = c;
    for (int i = 1; i < 9; i++) { float k = float(i) / 8. * uZB; acc += texture2D(tDiffuse, q - (q - .5) * k).rgb; }
    c = acc / 9.;
  }
  // a panel of the grid goes negative on the beat
  if (uPanels > 3.5 && abs(pid - mod(floor(uT * 2.53), 4.)) < .5) c = vec3(1.) - c;
  // a block or two flips to its negative, or to one channel
  float flip = step(1. - uBlock * .08, h(bq + uSeed * 2.1));
  c = mix(c, vec3(1.) - c.gbr, flip);
  c = mix(c, vec3(1.) - c, uInv);
  c *= uTint;
  // the tape's grade: washed blacks, a little less colour
  float Lg = dot(c, vec3(.3, .59, .11));
  c = mix(vec3(Lg), c, uSat);
  c = mix(c, vec3(.075, .095, .1) + c * .86, uLift);
  // the teal-and-salmon duotone of the music video
  if (uDuo > 0.) {
    float L = dot(c, vec3(.3, .59, .11));
    float warm = clamp(c.r - max(c.g, c.b) * .8, 0., 1.);
    vec3 d = mix(vec3(.03, .075, .08), vec3(.42, .5, .5), smoothstep(0., .55, L));
    d = mix(d, vec3(1., .94, .9), smoothstep(.55, .95, L));
    d = mix(d, vec3(1., .5, .42) * (.35 + L * .9), clamp(warm * 2.2, 0., 1.));
    c = mix(c, d, uDuo);
  }
  // datamosh: grey blocks left behind
  float gb = step(1. - uBlock * .06, h(floor(vUv * vec2(10., 7.)) + floor(uSeed / 3.) * 3.1));
  c = mix(c, vec3(.35, .37, .38) + (h(floor(gl_FragCoord.xy / 3.) + uSeed) - .5) * .15, gb * .85);
  // print halftone in the shadows
  if (uDot > 0.) {
    vec2 g = mat2(.707, -.707, .707, .707) * gl_FragCoord.xy / 5.;
    float L = dot(c, vec3(.3, .59, .11));
    float r = .5 * sqrt(clamp(1. - L * 1.6, 0., 1.));
    float dt = smoothstep(r + .08, r - .08, length(fract(g) - .5));
    c = mix(c, c * (1. - dt * .6), uDot);
  }
  // impact frames: a flat silhouette for a frame or two
  if (uImpact > .5) {
    float L = dot(c, vec3(.3, .59, .11));
    float m = smoothstep(.56, .66, L);
    if (uImpact < 1.5) c = mix(vec3(.92, .9, .86), vec3(.03, .05, .055), m);
    else c = mix(vec3(1., .55, .12), vec3(.02, .02, .02), m);
  }
  c = mix(c, vec3(.55 + .45 * h(floor(gl_FragCoord.xy / 2.) + uT * 9.)), inBand * .35);
  c += uFlash * (1. - step(.5, uImpact));
  c = mix(c, vec3(.02, .02, .025), divider);
  // letterbox
  float bar = step(.5 - uBox, abs(vUv.y - .5));
  float sl = .5 + .5 * sin(gl_FragCoord.y * 3.1416 * .5);
  c *= 1. - uScan * .22 * sl;
  vec2 v = vUv - .5;
  c *= 1. - uVig * smoothstep(.25, .85, length(v * vec2(1.2, 1.)));
  float g = h(floor(gl_FragCoord.xy / 1.5) + fract(uT * 7.31) * 113.) - .5;
  c += g * uGrain;
  c = mix(c, vec3(.0, .0, .005) + g * uGrain * .4, bar);
  gl_FragColor = vec4(c, 1.);
}`
  };

  // ---- trails: the last frames melt away along a flowing field (the "fluid")
  class TrailPass extends THREE.Pass {
    constructor() {
      super();
      const opt = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, type: THREE.HalfFloatType };
      this.a = new THREE.WebGLRenderTarget(4, 4, opt);
      this.b = new THREE.WebGLRenderTarget(4, 4, opt);
      this.mat = new THREE.ShaderMaterial({
        uniforms: { tCur: { value: null }, tPrev: { value: null }, uDecay: { value: 0 }, uFlow: { value: 0 }, uT: { value: 0 }, uZoom: { value: 0 }, uSpin: { value: 0 } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: /* glsl */`
uniform sampler2D tCur, tPrev; uniform float uDecay, uFlow, uT, uZoom, uSpin;
varying vec2 vUv;
float h21(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y); }
void main(){
  vec4 cur = texture2D(tCur, vUv);
  vec2 c = vUv - .5;
  float a = uSpin; c = mat2(cos(a), -sin(a), sin(a), cos(a)) * c * (1. - uZoom);
  vec2 q = c + .5;
  vec2 fl = vec2(noise(vUv * 4. + uT * .3), noise(vUv * 4. + 7.3 - uT * .3)) - .5;
  q += fl * uFlow;
  vec4 prev = texture2D(tPrev, q) * uDecay;
  gl_FragColor = vec4(max(cur.rgb, prev.rgb), 1.);
}`
      });
      this.quad = new THREE.FullScreenQuad(this.mat);
      this.copy = new THREE.FullScreenQuad(new THREE.ShaderMaterial({
        uniforms: { t: { value: null } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: 'uniform sampler2D t; varying vec2 vUv; void main(){ gl_FragColor = texture2D(t, vUv); }'
      }));
    }
    setSize(w, h) { this.a.setSize(w, h); this.b.setSize(w, h); }
    render(r, writeBuffer, readBuffer) {
      this.mat.uniforms.tCur.value = readBuffer.texture;
      this.mat.uniforms.tPrev.value = this.a.texture;
      r.setRenderTarget(this.b); this.quad.render(r);
      [this.a, this.b] = [this.b, this.a];
      this.copy.material.uniforms.t.value = this.a.texture;
      r.setRenderTarget(this.renderToScreen ? null : writeBuffer); this.copy.render(r);
    }
  }

  // datamosh: after a cut the old picture keeps moving, block by block, along made-up motion
  class MoshPass extends THREE.Pass {
    constructor() {
      super();
      const opt = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, type: THREE.HalfFloatType };
      this.prev = new THREE.WebGLRenderTarget(4, 4, opt);
      this.mat = new THREE.ShaderMaterial({
        uniforms: { tCur: { value: null }, tPrev: { value: null }, uAmt: { value: 0 }, uSeed: { value: 0 }, uRes: { value: new THREE.Vector2(1, 1) } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: /* glsl */`
uniform sampler2D tCur, tPrev; uniform float uAmt, uSeed; uniform vec2 uRes;
varying vec2 vUv;
float h(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float lum(vec3 c){ return dot(c, vec3(.3, .59, .11)); }
void main(){
  vec4 cur = texture2D(tCur, vUv);
  if (uAmt <= 0.) { gl_FragColor = cur; return; }
  vec2 bq = floor(vUv * uRes / 24.);
  float on = step(1. - uAmt * .75, h(bq + floor(uSeed / 2.) * .37));
  vec2 px = 3. / uRes;
  vec2 gr = vec2(lum(texture2D(tCur, vUv + vec2(px.x, 0.)).rgb) - lum(texture2D(tCur, vUv - vec2(px.x, 0.)).rgb),
                 lum(texture2D(tCur, vUv + vec2(0., px.y)).rgb) - lum(texture2D(tCur, vUv - vec2(0., px.y)).rgb));
  vec2 mv = gr * .03 + (vec2(h(bq + 1.), h(bq + 2.)) - .5) * .025;
  vec4 old = texture2D(tPrev, vUv - mv);
  gl_FragColor = mix(cur, old, on);
}`
      });
      this.quad = new THREE.FullScreenQuad(this.mat);
      this.copy = new THREE.FullScreenQuad(new THREE.ShaderMaterial({
        uniforms: { t: { value: null } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: 'uniform sampler2D t; varying vec2 vUv; void main(){ gl_FragColor = texture2D(t, vUv); }'
      }));
    }
    setSize(w, h) { this.prev.setSize(w, h); this.mat.uniforms.uRes.value.set(w, h); }
    render(r, writeBuffer, readBuffer) {
      this.mat.uniforms.tCur.value = readBuffer.texture;
      this.mat.uniforms.tPrev.value = this.prev.texture;
      r.setRenderTarget(this.renderToScreen ? null : writeBuffer); this.quad.render(r);
      // what we showed becomes what the next frame drags around
      this.copy.material.uniforms.t.value = writeBuffer.texture;
      r.setRenderTarget(this.prev); this.copy.render(r);
    }
  }
  let mosh;

  // the mirror: the video's kaleidoscope, applied to the world (the type is drawn after it, so it always reads)
  // x: 0 none, 1 left onto right, 2 four ways, 3 kaleidoscope of n slices, 4 three panels; y: 1 top reflected below
  const MIRROR = {
    uniforms: { tDiffuse: { value: null }, uX: { value: 0 }, uY: { value: 0 }, uN: { value: 6 }, uOff: { value: 0 }, uAsp: { value: 1.78 } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }',
    fragmentShader: /* glsl */`
uniform sampler2D tDiffuse; uniform float uX, uY, uN, uOff, uAsp;
varying vec2 vUv;
void main(){
  vec2 q = vUv;
  int mx = int(uX + .5);
  if (mx == 1) q.x = .5 - abs(q.x - .5);
  else if (mx == 2) q = .5 - abs(q - .5);
  else if (mx == 3) {
    vec2 c = (q - .5) * vec2(uAsp, 1.);
    float r = length(c), a = atan(c.y, c.x) + uOff, s = 6.2832 / uN;
    a = mod(a, s); a = abs(a - s * .5);
    q = r * vec2(cos(a), sin(a)) / vec2(uAsp, 1.) + .5;
  } else if (mx == 4) {
    float px = floor(q.x * 3.), fx = fract(q.x * 3.);
    if (abs(px - 1.) > .5) fx = 1. - fx;
    q.x = (1. + fx) / 3.;
  }
  if (uY > .5) q.y = .5 + abs(q.y - .5);
  gl_FragColor = texture2D(tDiffuse, clamp(q, .001, .999));
}`
  };
  let mirror;

  G.init = function (canvas) {
    const coarse = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 600;
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    renderer.setClearColor(0x000000, 1);
    renderer.autoClear = false;
    G.renderer = renderer;
    G.bg = () => bgMat;

    bgScene = new THREE.Scene();
    bgCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    bgMat = new THREE.ShaderMaterial({
      uniforms: {
        uRes: { value: new THREE.Vector2(1, 1) }, uT: { value: 0 }, uMode: { value: 0 }, uKick: { value: 0 }, uBeat: { value: 0 },
        uRoll: { value: 0 }, uZoom: { value: 1 }, uSeed: { value: 0 }, uAmt: { value: 1 }, uM: { value: 0 },
        uA: { value: new THREE.Vector3() }, uB: { value: new THREE.Vector3() }, uC: { value: new THREE.Vector3() }, uP: { value: new THREE.Vector4() }
      },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
      fragmentShader: BG_FRAG, depthTest: false, depthWrite: false
    });
    const bq = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), bgMat);
    bq.frustumCulled = false;
    bgScene.add(bq);

    scene = new THREE.Scene();
    tscene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(40, 1, 0.05, 200);
    G.camera = camera;
    G.D = 1 / Math.tan(20 * Math.PI / 180);      // at this distance the screen is 2 units tall

    // the type and shapes
    const A = H.Atlas;
    const tex = new THREE.DataTexture(A.data, A.W, A.H, THREE.RedFormat, THREE.UnsignedByteType);
    tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false; tex.needsUpdate = true;
    const pl = new THREE.PlaneGeometry(2, 2);
    for (const Lr of LAYERS) {
      const geo = Lr.geo = new THREE.InstancedBufferGeometry();
      geo.index = pl.index; geo.attributes.position = pl.attributes.position; geo.attributes.uv = pl.attributes.uv;
      const add = (name, arr, size) => {
        const at = new THREE.InstancedBufferAttribute(arr, size);
        at.setUsage(THREE.DynamicDrawUsage);
        geo.setAttribute(name, at); Lr.attrs[name] = at;
      };
      add('aC', Lr.C, 3); add('aX', Lr.X, 3); add('aY', Lr.Y, 3); add('aUV', Lr.UV, 4); add('aCol', Lr.COL, 4); add('aSt', Lr.ST, 4); add('aFx', Lr.FX, 4);
      geo.instanceCount = 0;
    }
    const mat = new THREE.ShaderMaterial({
      uniforms: { uAtlas: { value: tex }, uEdge: { value: A.edge }, uT: { value: 0 } },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
      extensions: { derivatives: true }
    });
    G.mat = mat;
    LAYERS.forEach((Lr, i) => { const m = new THREE.Mesh(Lr.geo, mat); m.frustumCulled = false; (i ? tscene : scene).add(m); });

    composer = new THREE.EffectComposer(renderer);
    const bp = new THREE.RenderPass(bgScene, bgCam);
    composer.addPass(bp);
    const mp = new THREE.RenderPass(scene, camera); mp.clear = false;
    composer.addPass(mp);
    trail = new TrailPass(); composer.addPass(trail);
    bloom = new THREE.UnrealBloomPass(new THREE.Vector2(256, 256), 0.7, 0.5, 0.75);
    composer.addPass(bloom);
    mirror = new THREE.ShaderPass(MIRROR); composer.addPass(mirror);
    const tp = new THREE.RenderPass(tscene, camera); tp.clear = false; tp.clearDepth = true;
    composer.addPass(tp);
    mosh = new MoshPass(); composer.addPass(mosh);
    finish = new THREE.ShaderPass(FINISH); composer.addPass(finish);
    G.dprMax = coarse ? 1.25 : 1.5;
    G.resize(); addEventListener('resize', G.resize);
  };

  G.resize = function () {
    dpr = Math.min(devicePixelRatio || 1, G.dprMax || 1.5);
    W = innerWidth; Hh = innerHeight;
    renderer.setPixelRatio(dpr); renderer.setSize(W, Hh, false);
    composer.setPixelRatio(dpr); composer.setSize(W, Hh);
    camera.aspect = W / Hh; camera.updateProjectionMatrix();
    bgMat.uniforms.uRes.value.set(W * dpr, Hh * dpr);
    finish.uniforms.uRes.value.set(W * dpr, Hh * dpr);
    G.aspect = W / Hh;
    mirror.uniforms.uAsp.value = W / Hh;
  };

  // st: { t, bg:{mode,a,b,c,p,kick,beat,roll,zoom,amt}, cam:{x,y,z,roll,fov,lx,ly}, post:{...}, trail:{decay,flow,zoom,spin}, bloom:{s,r,th} }
  G.render = function (st) {
    if (!renderer) return;
    for (const Lr of LAYERS) {
      for (const k in Lr.attrs) { const at = Lr.attrs[k]; at.updateRange.offset = 0; at.updateRange.count = Lr.n * at.itemSize; at.needsUpdate = true; }
      Lr.geo.instanceCount = Lr.n;
    }
    G.mat.uniforms.uT.value = st.t;
    const u = bgMat.uniforms, bg = st.bg;
    u.uT.value = st.t; u.uMode.value = bg.mode; u.uKick.value = bg.kick || 0; u.uBeat.value = bg.beat || 0;
    u.uRoll.value = bg.roll || 0; u.uM.value = bg.m || 0; u.uZoom.value = bg.zoom || 1; u.uAmt.value = bg.amt == null ? 1 : bg.amt;
    u.uA.value.set(...bg.a); u.uB.value.set(...bg.b); u.uC.value.set(...bg.c); u.uP.value.set(...(bg.p || [0, 0, 0, 0]));
    const c = st.cam;
    camera.fov = c.fov || 40; camera.updateProjectionMatrix();
    camera.position.set(c.x || 0, c.y || 0, (c.z == null ? G.D : c.z));
    camera.up.set(Math.sin(c.roll || 0), Math.cos(c.roll || 0), 0);
    camera.lookAt(c.lx || 0, c.ly || 0, c.lz == null ? 0 : c.lz);
    const tr = trail.mat.uniforms, T = st.trail || {};
    tr.uDecay.value = T.decay || 0; tr.uFlow.value = T.flow || 0; tr.uT.value = st.t; tr.uZoom.value = T.zoom || 0; tr.uSpin.value = T.spin || 0;
    const bl = st.bloom || {};
    bloom.strength = bl.s == null ? 0.7 : bl.s; bloom.radius = bl.r == null ? 0.5 : bl.r; bloom.threshold = bl.th == null ? 0.75 : bl.th;
    const f = finish.uniforms, P = st.post || {};
    f.uT.value = st.t; f.uSeed.value = P.seed || 0; f.uRGB.value = P.rgb || 0; f.uSlice.value = P.slice || 0; f.uBlock.value = P.block || 0;
    f.uFlash.value = P.flash || 0; f.uInv.value = P.inv || 0; f.uScan.value = P.scan == null ? 0.3 : P.scan; f.uGrain.value = P.grain == null ? 0.06 : P.grain;
    f.uVig.value = P.vig == null ? 0.3 : P.vig; f.uDot.value = P.dot || 0; f.uDuo.value = P.duo || 0;
    f.uVhs.value = P.vhs == null ? 0.5 : P.vhs; f.uLift.value = P.lift == null ? 0.5 : P.lift; f.uSat.value = P.sat == null ? 0.8 : P.sat;
    const M = st.mirror || {}, mu = mirror.uniforms;
    mu.uX.value = M.x || 0; mu.uY.value = M.y || 0; mu.uN.value = M.n || 6; mu.uOff.value = M.off || 0;
    f.uPanels.value = P.panels || 0; f.uZB.value = P.zb || 0; f.uImpact.value = P.impact || 0; f.uBox.value = P.box || 0;
    mosh.mat.uniforms.uAmt.value = P.mosh || 0; mosh.mat.uniforms.uSeed.value = P.seed || 0;
    f.uTint.value.set(...(P.tint || [1, 1, 1]));
    renderer.clear();
    composer.render();
  };
})();
